import * as React from 'react';
import {
  Box,
  StackLayout,
  StatusBanner,
  StatusBadge,
  UtilityText,
  Icon,
  TextAction,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { Link } from 'react-router-dom';
import { useIntl } from '@leagueplatform/locales';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible';
import { FullscreenConfirmationModal } from '../fullscreen-confirmation-modal/fullscreen-confirmation-modal.component';
import type { DeepPublishTreeNode } from '../deep-publish-confirmation-modal/deep-publish-confirmation-modal.component';
import {
  getStatusBadge,
  DeepPublishTreeItem,
} from '../deep-publish-confirmation-modal/deep-publish-confirmation-modal.component';

export interface BulkPublishRootNode {
  rootEntityId: string;
  rootEntityName: string;
  rootDomainEntityType: string;
  rootStatus: string;
  rootLink?: string;
  treeNode: DeepPublishTreeNode;
  errorCount: number;
}

export interface BulkDeepPublishConfirmationModalProps {
  isOpen: boolean;
  isLoading: boolean;
  rootNodes: BulkPublishRootNode[];
  errorMessage?: string;
  hasFailures: boolean;
  totalDraftCount: number;
  discoveredSoFar: number;
  onConfirm: () => void;
  onClose: () => void;
}

const BulkPublishRootRow = ({
  rootNode,
}: {
  rootNode: BulkPublishRootNode;
}) => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = React.useState(false);
  const badgeProps = getStatusBadge(rootNode.rootStatus);
  const badge = {
    genesisStatus: badgeProps.genesisStatus,
    label: formatMessage({ id: badgeProps.labelKey }),
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Box
        as={CollapsibleTrigger}
        css={{
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: '$button',
          width: '100%',
          cursor: 'pointer',
          padding: '$half',
          '&:hover': {
            backgroundColor: '$surfaceBackgroundTertiary',
          },
        }}
      >
        <StackLayout
          orientation="horizontal"
          spacing="$half"
          verticalAlignment="center"
        >
          <Icon
            icon="interfaceChevronRight"
            size="$threeQuarters"
            css={{
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 150ms ease',
            }}
          />
          <StatusBadge status={badge.genesisStatus} label={badge.label} />
          {rootNode.rootDomainEntityType && (
            <UtilityText css={{ typography: '$overline' }}>
              {rootNode.rootDomainEntityType}
            </UtilityText>
          )}
          {rootNode.rootLink ? (
            <TextAction
              as={Link}
              to={rootNode.rootLink}
              target="_blank"
              rel="noopener noreferrer"
              indicator="externalLink"
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
            >
              {rootNode.rootEntityName}
            </TextAction>
          ) : (
            <UtilityText>{rootNode.rootEntityName}</UtilityText>
          )}
          {rootNode.errorCount > 0 && (
            <StatusBadge
              status="error"
              label={formatMessage(
                { id: 'DEEP_PUBLISH_CHILD_ENTITIES_COUNT' },
                { count: rootNode.errorCount },
              )}
            />
          )}
        </StackLayout>
      </Box>
      <CollapsibleContent>
        <Box css={{ paddingInlineStart: '$two', paddingBlockEnd: '$half' }}>
          {rootNode.treeNode.childNodes.map((childNode, index) => {
            const key = `${childNode.id}-${index}`;
            return (
              <DeepPublishTreeItem
                key={key}
                node={childNode}
                duplicateIndicatorMessageId="BULK_DEEP_PUBLISH_ALREADY_INCLUDED"
              />
            );
          })}
        </Box>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const BulkDeepPublishConfirmationModal = ({
  isOpen,
  isLoading,
  rootNodes,
  errorMessage,
  hasFailures,
  totalDraftCount,
  discoveredSoFar,
  onConfirm,
  onClose,
}: BulkDeepPublishConfirmationModalProps) => {
  const { formatMessage } = useIntl();
  const isReady = !isLoading && rootNodes.length > 0;
  const hasErrors = Boolean(errorMessage) || hasFailures;
  const isConfirmDisabled = hasErrors || !isReady;

  return (
    <FullscreenConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      titleId="BULK_DEEP_PUBLISH_MODAL_TITLE"
      descriptionId="BULK_DEEP_PUBLISH_MODAL_DESCRIPTION"
      isLoading={isLoading}
      loadingTextId="BULK_DEEP_PUBLISH_FETCHING_TEXT"
      loadingContent={
        discoveredSoFar > 0 ? (
          <UtilityText emphasis="subtle">
            {formatMessage(
              { id: 'BULK_DEEP_PUBLISH_FETCHING_COUNT' },
              { count: discoveredSoFar },
            )}
          </UtilityText>
        ) : undefined
      }
      confirmLabel={formatMessage(
        { id: 'BULK_DEEP_PUBLISH_CONFIRM_BUTTON' },
        { count: totalDraftCount },
      )}
      onConfirm={onConfirm}
      isConfirmDisabled={isConfirmDisabled}
    >
      {hasFailures && (
        <StatusBanner status="error" css={{ flex: 'none' }}>
          <UtilityText as="p">
            {formatMessage({
              id: 'DEEP_PUBLISH_DISCOVERY_FAILURE_ERROR',
            })}
          </UtilityText>
        </StatusBanner>
      )}
      {errorMessage && (
        <ParagraphText css={{ color: '$onSurfaceTextCritical' }}>
          {errorMessage}
        </ParagraphText>
      )}
      {isReady &&
        rootNodes.map((rootNode) => (
          <BulkPublishRootRow key={rootNode.rootEntityId} rootNode={rootNode} />
        ))}
    </FullscreenConfirmationModal>
  );
};
